import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// First-party Google OAuth callback landing route (/auth/google).
// Google redirects here with ?code&state; we verify state locally and hand the
// authorization code to the backend for a secure server-side exchange.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleCallback() {
  const { loginWithGoogleCode } = useAuth();
  const navigate = useNavigate();
  const [err, setErr] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const p = new URLSearchParams(window.location.search);
    const code = p.get("code");
    const state = p.get("state");
    const gerr = p.get("error");
    const savedState = sessionStorage.getItem("arca_g_state");
    const nonce = sessionStorage.getItem("arca_g_nonce");
    sessionStorage.removeItem("arca_g_state");
    sessionStorage.removeItem("arca_g_nonce");

    if (gerr) { setErr("Google sign-in was cancelled."); return; }
    if (!code || !state || !savedState || state !== savedState) {
      setErr("Google sign-in could not be verified. Please try again.");
      return;
    }
    loginWithGoogleCode({ code, nonce })
      .then(() => { toast.success("Signed in with Google."); navigate("/studio", { replace: true }); })
      .catch((e) => setErr(e.message || "Google sign-in failed. Please try again."));
  }, [loginWithGoogleCode, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" data-testid="google-callback">
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1/4 w-[36rem] h-[36rem] bg-[#0055FF]/15 blur-[130px] rounded-full" />
      <div className="relative">
        {!err ? (
          <div className="flex flex-col items-center gap-4 text-slate-300" data-testid="google-callback-loading">
            <Loader2 size={30} className="animate-spin text-[#00F0FF]" />
            <p className="font-mono text-xs uppercase tracking-widest">Completing sign-in…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4" data-testid="google-callback-error">
            <p className="text-white text-lg font-display font-bold">Sign-in problem</p>
            <p className="text-slate-400 text-sm max-w-sm">{err}</p>
            <Link to="/login" className="mt-2 bg-[#0055FF] text-white rounded-xl px-5 py-2.5 font-semibold hover:bg-[#0044dd] transition-colors" data-testid="google-callback-back">Back to login</Link>
          </div>
        )}
      </div>
    </div>
  );
}
